import { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import { HeartIcon } from "@/components/icons/heart-icon";
import { MealPlaceholderIcon } from "@/components/icons/meal-placeholder-icon";
import ImageWithLoading from "@/components/image/image-with-loading";
import { FilledButton } from "@/components/ui/buttons";
import { SavedMealsTabValue } from "@/features/meal-plan/constants/saved-meals-tabs";
import { useGetMyFoods } from "@/hooks/requests/my-foods/use-get-my-foods";
import { useRemoveMyFood } from "@/hooks/requests/my-foods/use-remove-my-food";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, MouseEvent, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useSwipe } from "../meal-card/use-swipe";
import { RemoveMealConfirmModal } from "../remove-meal-confirm-modal";

interface FoodLibraryListProps {
  tab: SavedMealsTabValue;
  searchQuery?: string;
  onClearSearch?: () => void;
  renderMealAction?: (meal: FoodLibraryMeal) => ReactNode;
  onMealClick?: (meal: FoodLibraryMeal) => void;
  variant?: "flat";
}

const EMPTY_STATES: Record<SavedMealsTabValue, { title: string; description: string }> = {
  "your-food": {
    title: "NO RECENT FOODS",
    description: "Foods you add or swap into your plan will appear here.",
  },
  "most-used": {
    title: "NO MOST USED FOODS",
    description: "Foods you use in your plan will appear here.",
  },
};

const timestamp = (value?: string | null) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const compareByName = (a: FoodLibraryMeal, b: FoodLibraryMeal) => a.name.localeCompare(b.name);

const sortMeals = (meals: FoodLibraryMeal[], tab: SavedMealsTabValue) => {
  return [...meals].sort((a, b) => {
    const useCountDifference = (b.useCount ?? 0) - (a.useCount ?? 0);
    if (tab === "most-used" && useCountDifference) {
      return useCountDifference;
    }

    const timestampDifference = timestamp(b.lastUsedAt) - timestamp(a.lastUsedAt);
    return timestampDifference || compareByName(a, b);
  });
};

interface FoodLibraryListItemProps {
  meal: FoodLibraryMeal;
  renderMealAction?: (meal: FoodLibraryMeal) => ReactNode;
  onRemove?: (meal: FoodLibraryMeal) => Promise<void>;
  onMealClick?: (meal: FoodLibraryMeal) => void;
  isRemoving: boolean;
  isRemovalPending: boolean;
  variant?: "flat";
}

const formatMacro = (label: string, value?: number | null) =>
  value == null ? null : `${label}: ${value}g`;

const FoodLibraryListItem = memo(function FoodLibraryListItem({
  meal,
  renderMealAction,
  onRemove,
  onMealClick,
  isRemoving,
  isRemovalPending,
  variant,
}: FoodLibraryListItemProps) {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const ignoreClickRef = useRef(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const mealName = meal.name ?? "";
  const nutrition = meal.nutritionalInfo?.perPortion;
  const macroSummary = [
    nutrition?.energy?.kcal != null ? `${nutrition.energy.kcal} kcal` : null,
    formatMacro("P", nutrition?.protein?.grams),
    formatMacro("C", nutrition?.carbohydrate?.total?.grams),
    formatMacro("F", nutrition?.fat?.total?.grams),
  ].filter((value): value is string => value !== null);

  const resetBackground = useCallback(() => {
    if (backgroundRef.current) {
      backgroundRef.current.style.width = "0px";
    }
  }, []);

  const handleSwipeLeft = useCallback(() => {
    ignoreClickRef.current = true;
    setIsConfirmModalOpen(true);
    resetBackground();
  }, [resetBackground]);

  const handleSwipeMove = useCallback((deltaX: number) => {
    if (backgroundRef.current) {
      backgroundRef.current.style.width = `${Math.min(Math.abs(deltaX), 120)}px`;
    }
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!onRemove) return;
    ignoreClickRef.current = false;
    setIsConfirmModalOpen(false);
    resetBackground();
    await onRemove(meal);
  }, [meal, onRemove, resetBackground]);

  const handleCancelRemove = useCallback(() => {
    ignoreClickRef.current = false;
    setIsConfirmModalOpen(false);
    resetBackground();
  }, [resetBackground]);

  const handleMealClick = useCallback(() => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }

    onMealClick?.(meal);
  }, [meal, onMealClick]);

  const handleClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!ignoreClickRef.current) return;

    ignoreClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const { ref: swipeRef } = useSwipe({
    onSwipeLeft: handleSwipeLeft,
    onSwipeMove: handleSwipeMove,
    onSwipeEnd: resetBackground,
    threshold: 80,
    velocityThreshold: 0.5,
    disabled: !onRemove || isRemovalPending,
  });

  if (variant === "flat") {
    return (
      <>
        <div className="relative overflow-hidden">
          <div
            ref={backgroundRef}
            className="bg-toast-error-default absolute top-0 right-0 bottom-0 w-0 overflow-hidden text-white"
          >
            <div className="flex h-full items-center justify-end pr-3">
              <div className="flex flex-col items-center gap-1 px-3">
                <HeartIcon className="h-5 w-5 fill-white" />
                <span className={cn(textVariants.footnote, "text-white")}>Remove</span>
              </div>
            </div>
          </div>

          <div ref={swipeRef} className="relative z-10 bg-white">
            <div
              className="flex h-[68px] items-center gap-3 border-b border-[rgba(214,219,231,0.5)] pr-1"
              onClickCapture={handleClickCapture}
              onClick={handleMealClick}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="overflow-hidden text-[16px] leading-normal font-bold text-ellipsis whitespace-nowrap text-black">
                  {mealName}
                </p>
                {macroSummary.length > 0 && (
                  <span className="overflow-hidden text-[13px] leading-normal font-medium text-ellipsis whitespace-nowrap text-[#7485a7]">
                    {macroSummary.join("   ")}
                  </span>
                )}
              </div>
              {renderMealAction && (
                <div onClick={(e) => e.stopPropagation()}>{renderMealAction(meal)}</div>
              )}
            </div>
          </div>
        </div>

        <RemoveMealConfirmModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
          isRemoving={isRemoving}
        />
      </>
    );
  }

  return (
    <>
      <div className="relative rounded-2xl">
        <div
          ref={backgroundRef}
          className="bg-toast-error-default absolute top-0 right-0 bottom-0 m-px w-0 overflow-hidden rounded-2xl text-white"
        >
          <div className="flex h-full items-center justify-end pr-4">
            <div className="flex flex-col items-center gap-2 px-5">
              <HeartIcon className="h-5 w-5 fill-white" />
              <span className={cn(textVariants.footnote, "text-white")}>Remove</span>
            </div>
          </div>
        </div>

        <div ref={swipeRef} className="relative z-10">
          <div className="flex min-h-16 items-center gap-4 rounded-2xl bg-white px-1 py-1">
            <ImageWithLoading
              src={meal.imageUri ?? ""}
              alt={mealName}
              notFoundImage={
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                  <MealPlaceholderIcon className="h-5 w-5" />
                </div>
              }
              errorImage={
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                  <MealPlaceholderIcon className="h-5 w-5" />
                </div>
              }
              classNames={{
                container: "h-14 w-14 shrink-0 overflow-hidden rounded-full",
                image: "object-cover",
              }}
            />

            <div className="min-w-0 flex-1 text-left text-black">
              <h4 className={cn(textVariants.body, "line-clamp-1")}>{mealName}</h4>
              {macroSummary.length > 0 ? (
                <span className={cn(textVariants.footnote, "line-clamp-1 text-gray-500")}>
                  {macroSummary.join(" · ")}
                </span>
              ) : meal.brandName ? (
                <span className={cn(textVariants.footnote, "line-clamp-1 text-gray-500")}>
                  {meal.brandName}
                </span>
              ) : null}
            </div>

            {renderMealAction?.(meal)}
          </div>
        </div>
      </div>

      <RemoveMealConfirmModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        isRemoving={isRemoving}
      />
    </>
  );
});

export const FoodLibraryList = memo(function FoodLibraryList({
  tab,
  searchQuery = "",
  onClearSearch,
  renderMealAction,
  onMealClick,
  variant,
}: FoodLibraryListProps) {
  const { myFoods, isLoading } = useGetMyFoods();
  const { removeMyFoodAsync } = useRemoveMyFood();
  const { showError } = useToast();
  const [removingMealId, setRemovingMealId] = useState<string | null>(null);

  const meals = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    const filteredMeals = myFoods.filter((meal) => {
      if (!query) return true;
      return [meal.name, meal.brandName].some((value) =>
        value?.toLocaleLowerCase().includes(query),
      );
    });

    return sortMeals(filteredMeals, tab);
  }, [myFoods, searchQuery, tab]);

  const handleRemove = useCallback(
    async (meal: FoodLibraryMeal) => {
      if (!meal.id) return;

      setRemovingMealId(meal.id);
      try {
        await removeMyFoodAsync(meal.id);
      } catch (error) {
        console.error("Failed to remove food from library:", error);
        showError("Failed to remove food. Please try again.");
      } finally {
        setRemovingMealId(null);
      }
    },
    [removeMyFoodAsync, showError],
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <IonSkeletonText key={index} animated className="h-16 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (meals.length === 0) {
    if (searchQuery.trim()) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 py-20 text-center">
          <h3 className={cn(textVariants.serif, "text-3xl")}>Could not find the meal</h3>
          <p className={cn(textVariants.body, "max-w-60 text-black/70")}>
            Try a different search term.
          </p>
          <FilledButton
            color="black"
            textCase="normal"
            className="bg-os-brand hover:bg-os-brand active:bg-os-brand mt-4 w-fit px-4"
            onClick={onClearSearch}
          >
            Clear search
          </FilledButton>
        </div>
      );
    }

    const emptyState = EMPTY_STATES[tab];
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-20 text-center">
        <h3 className={cn(textVariants.body, "text-black")}>{emptyState.title}</h3>
        <p className={cn(textVariants.bodySmall, "max-w-60 text-black/70")}>
          {emptyState.description}
        </p>
      </div>
    );
  }

  return (
    <div className={variant === "flat" ? "pb-6" : "space-y-3 pb-6"}>
      {meals.map((meal, index) => {
        const mealName = meal.name ?? "";
        const rowKey = meal.id ?? `${mealName}-${index}`;

        const canRemove = Boolean(meal.id);

        return (
          <FoodLibraryListItem
            key={rowKey}
            meal={meal}
            renderMealAction={renderMealAction}
            onRemove={canRemove ? handleRemove : undefined}
            onMealClick={onMealClick}
            isRemoving={removingMealId === meal.id}
            isRemovalPending={removingMealId !== null}
            variant={variant}
          />
        );
      })}
    </div>
  );
});
